import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchQuickPassPings,
  markPingViewedRemote,
  respondToPingRemote,
} from '../_data/quickpass-pings'
import {
  PingsLoadState,
  QuickPassPing,
  UseQuickPassPingsValue,
} from '../_types/quickpass-ping'
import { useAuth } from './use-auth'

/**
 * Lightweight ping store for the renter side. Loads on mount, tracks
 * pending count for the header badge, exposes refresh + viewed +
 * respond mutations that update local state optimistically.
 *
 * @returns {UseQuickPassPingsValue}
 */
const useQuickPassPings = (): UseQuickPassPingsValue => {
  const { quickPassUser } = useAuth()
  const userId = quickPassUser?.id ?? ''

  const [state, setState] = useState<PingsLoadState>('idle')
  const [pings, setPings] = useState<QuickPassPing[]>([])
  const [errorMessage, setErrorMessage] = useState<string>('')

  const refresh = useCallback(async (): Promise<void> => {
    if (userId === '') {
      return
    }
    setState('loading')
    setErrorMessage('')

    try {
      const fresh = await fetchQuickPassPings(userId)
      setPings(fresh)
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error ? error.message : 'Could not load your pings.',
      )
    }
  }, [userId])

  useEffect(() => {
    if (userId === '') {
      return
    }
    void refresh()
  }, [userId, refresh])

  const markViewed = useCallback(
    async (pingId: string): Promise<void> => {
      if (userId === '') {
        return
      }
      setPings(currentPings =>
        currentPings.map(ping => {
          if (ping.id !== pingId) {
            return ping
          }
          if (ping.status !== 'pending') {
            return ping
          }
          return {
            ...ping,
            status: 'viewed',
            viewedAt: Date.now(),
          }
        }),
      )
      try {
        await markPingViewedRemote(userId, pingId)
      } catch (error) {
        console.error('[quickpass-pings] markViewed failed', error)
      }
    },
    [userId],
  )

  const respond = useCallback(
    async (
      pingId: string,
      response: 'interested' | 'not-interested',
    ): Promise<void> => {
      if (userId === '') {
        return
      }

      const previousState = pings
      setPings(currentPings =>
        currentPings.map(ping => {
          if (ping.id !== pingId) {
            return ping
          }
          return {
            ...ping,
            status: response,
            respondedAt: Date.now(),
          }
        }),
      )

      try {
        await respondToPingRemote(userId, pingId, response)
      } catch (error) {
        console.error('[quickpass-pings] respond failed', error)
        setPings(previousState)
        throw error
      }
    },
    [userId, pings],
  )

  const pendingCount = useMemo(
    () => pings.filter(ping => ping.status === 'pending').length,
    [pings],
  )

  return {
    state,
    pings,
    pendingCount,
    errorMessage,
    refresh,
    markViewed,
    respond,
  }
}

export { useQuickPassPings }
